import { PHOTOS } from "@/assets/photos";
import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";

import { PageHero, Section } from "@/components/site/section";
import { FixtureCard, NewsCard, StatTile } from "@/components/site/cards";
import { fixturesQuery, newsByCategoryQuery, squadQuery } from "@/lib/site-queries";

const HOST_CITIES = [
  "Atlanta",
  "Boston",
  "Dallas",
  "Guadalajara",
  "Houston",
  "Kansas City",
  "Los Angeles",
  "Mexico City",
  "Miami",
  "Monterrey",
  "New York/New Jersey",
  "Philadelphia",
  "San Francisco Bay Area",
  "Seattle",
  "Toronto",
  "Vancouver",
];

export const Route = createFileRoute("/national-team/world-cup")({
  head: () => ({
    meta: [
      { title: "Argentina World Cup | Argentinian.Soccer" },
      { name: "description", content: "Argentina at the World Cup: news, fixtures tree, squad and statistics." },
      { property: "og:title", content: "Argentina World Cup | Argentinian.Soccer" },
      { property: "og:url", content: "/national-team/world-cup" },
    ],
    links: [{ rel: "canonical", href: "/national-team/world-cup" }],
  }),
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(newsByCategoryQuery("national_team", 4)),
      context.queryClient.ensureQueryData(fixturesQuery("world_cup", 12)),
      context.queryClient.ensureQueryData(squadQuery("senior")),
    ]),
  component: WCPage,
});

function WCPage() {
  const { data: news } = useSuspenseQuery(newsByCategoryQuery("national_team", 4));
  const { data: fixtures } = useSuspenseQuery(fixturesQuery("world_cup", 12));
  const { data: squad } = useSuspenseQuery(squadQuery("senior"));

  return (
    <>
      <PageHero eyebrow="National Team" title="Argentina World Cup" description="Three stars and counting." image={PHOTOS.wcAustralia} />

      <Section id="tournament-guide" eyebrow="2026 guide" title="Expanded World Cup format">
        <div className="grid gap-4 md:grid-cols-4">
          <StatTile label="Teams" value="48" sub="first expanded edition" />
          <StatTile label="Groups" value="12" sub="four teams each" />
          <StatTile label="Matches" value="104" sub="across North America" />
          <StatTile label="Final" value="NY/NJ" sub="New York New Jersey Stadium" />
        </div>
        <div className="mt-6 rounded-xl border border-border bg-card p-5">
          <h2 className="font-display text-2xl text-navy">Public tournament facts</h2>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            The 2026 FIFA World Cup is hosted by Canada, Mexico and the United States, with
            the opening match scheduled for Mexico City and the final scheduled for New York/New Jersey.
            The top two teams from each group plus the eight best third-placed teams advance into the knockout bracket.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {HOST_CITIES.map((city) => (
              <span
                key={city}
                className="rounded-full border border-border bg-secondary px-3 py-1 font-condensed text-xs font-bold uppercase tracking-wider text-secondary-foreground"
              >
                {city}
              </span>
            ))}
          </div>
        </div>
      </Section>

      <Section id="news" eyebrow="World Cup" title="News">
        <div className="grid gap-5 md:grid-cols-2">
          {news.map((a) => <NewsCard key={a.id} article={a} />)}
        </div>
      </Section>

      <Section id="fixtures-tree" eyebrow="Fixtures" title="Fixtures-Tree">
        <h3 className="mb-4 font-display text-lg font-bold text-navy">Group</h3>
        <div className="grid gap-5 md:grid-cols-2">
          {fixtures.map((f) => <FixtureCard key={f.id} fixture={f} />)}
        </div>
      </Section>

      <Section id="squad" eyebrow="World Cup" title="Squad">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {squad.slice(0, 12).map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-lg border border-border bg-card p-4">
              <span className="font-medium text-navy">{p.name}</span>
              <span className="font-display text-xl font-bold text-primary">{p.shirt_number}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section id="statistics" eyebrow="World Cup" title="Statistics">
        <div className="grid gap-4 md:grid-cols-3">
          <StatTile label="World Cup titles" value="3" sub="1978 · 1986 · 2022" />
          <StatTile label="Finals reached" value="6" />
          <StatTile label="Top scorer" value="Batistuta" sub="10 WC goals" />
        </div>
      </Section>
    </>
  );
}
